import { FileWithPath, Dropzone as MantineDropzone } from "@mantine/dropzone";
import { IconDownload, IconX } from "@tabler/icons-react";
import { t } from "i18next";

import { colors } from "#app/theme/v1/colors";
import { useDropzoneHandlers, DROPZONE_MAX_SIZE } from "#shared/hooks/useDropzoneHandlers";
import { Paper } from "#shared/view/components/Paper";
import { ProgressBar } from "#shared/view/components/ProgressBar";
import SimpleDropzoneStatus from "#shared/view/components/SimpleDrozone/SimpleDropzoneStatus";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";

import classes from "./SimpleDropzone.module.css";

type Props = {
  onDrop: (files: FileWithPath[]) => void;
  title: string;
  subTitle: string;
  validExtensions: string[];
  accept: Record<string, string[]>;
  maxSize?: number;
  multiple?: boolean;
  loading?: boolean;
  uploadProgress?: number;
  progressBarColor?: string;
};

const PROGRESS_BAR_HEIGHT = "25px";

export const SimpleDropzone = ({
  onDrop,
  title,
  subTitle,
  validExtensions,
  accept,
  maxSize = DROPZONE_MAX_SIZE,
  multiple = true,
  loading,
  uploadProgress,
  progressBarColor = colors.blue[5],
}: Props) => {
  const { inputAccept, validator, handleReject } = useDropzoneHandlers(accept, validExtensions);

  const shouldShowProgressBar = !!uploadProgress && uploadProgress > 0 && uploadProgress < 100;

  return (
    <MantineDropzone
      onDrop={onDrop}
      onReject={handleReject}
      disabled={shouldShowProgressBar}
      maxSize={maxSize}
      className={classes.root}
      validator={validator}
      multiple={multiple}
      loading={loading}
      inputProps={{ accept: inputAccept }}
    >
      {!shouldShowProgressBar && (
        <MantineDropzone.Accept>
          <SimpleDropzoneStatus title={title} Icon={IconDownload} />
        </MantineDropzone.Accept>
      )}

      <MantineDropzone.Reject>
        <SimpleDropzoneStatus title="Type de fichier invalide" Icon={IconX} />
      </MantineDropzone.Reject>

      {!shouldShowProgressBar && (
        <MantineDropzone.Idle>
          <SimpleDropzoneStatus title={title} Icon={IconDownload} />
        </MantineDropzone.Idle>
      )}

      {!shouldShowProgressBar && (
        <Stack align="center" gap="xxs">
          {!!subTitle && (
            <Text size="sm" c="dimmed" inline mt={7}>
              {subTitle}
            </Text>
          )}
        </Stack>
      )}

      {shouldShowProgressBar && (
        <MantineDropzone.Idle>
          <Paper h={PROGRESS_BAR_HEIGHT} w={"100%"} p="0" mt="xs">
            <ProgressBar
              style={{ height: PROGRESS_BAR_HEIGHT }}
              value={uploadProgress}
              label={`${t("qr.dropzone.upload")} : ${uploadProgress} %`}
              animated={uploadProgress < 100}
              color={progressBarColor}
            />
          </Paper>
        </MantineDropzone.Idle>
      )}
    </MantineDropzone>
  );
};
